import { z } from 'zod';
import type { SourceFact } from './assistant';
import { formatExactMoney, moneyMinorStringSchema } from './money';

const optionalPrice = moneyMinorStringSchema.nullish();
const offeringPrice = z.object({
  code: z.string(),
  pricePenMinor: optionalPrice,
  priceUsdMinor: optionalPrice,
  currentPriceAndAvailability: z.string().optional(),
});

interface PricedOffering {
  fact: SourceFact;
  price: z.infer<typeof offeringPrice>;
}

// Arithmetic is application evidence, with the original operands attached.
// It never establishes the commercial reason for a difference in prices.
export function offeringPriceComparisons(
  facts: readonly SourceFact[],
  references: readonly string[],
  now: string
): SourceFact[] {
  const offers: PricedOffering[] = [];
  for (const fact of facts) {
    if (
      fact.private ||
      Date.parse(now) - Date.parse(fact.observedAt) > 300_000 ||
      Date.parse(fact.observedAt) > Date.parse(now)
    )
      continue;
    let raw: unknown;
    try {
      raw = JSON.parse(fact.content);
    } catch {
      continue;
    }
    const parsed = offeringPrice.safeParse(raw);
    if (
      !parsed.success ||
      parsed.data.currentPriceAndAvailability === 'unknown'
    )
      continue;
    if (!references.includes(fact.id) && !references.includes(parsed.data.code))
      continue;
    offers.push({ fact, price: parsed.data });
  }
  const comparisons: SourceFact[] = [];
  for (let left = 0; left < offers.length; left += 1) {
    for (const right of offers.slice(left + 1)) {
      const first = offers[left]!;
      for (const currency of ['PEN', 'USD'] as const) {
        const field = currency === 'PEN' ? 'pricePenMinor' : 'priceUsdMinor';
        const a = first.price[field],
          b = right.price[field];
        if (a == null || b == null) continue;
        const difference = BigInt(a) - BigInt(b);
        const amount = difference < 0n ? -difference : difference;
        const serializedAmount = amount.toString();
        comparisons.push({
          id: `price-difference:${first.fact.id}:${right.fact.id}:${currency}`,
          source: 'system:exact-offering-price-comparison',
          version: `${first.fact.version}:${right.fact.version}`.slice(0, 200),
          observedAt:
            first.fact.observedAt < right.fact.observedAt
              ? first.fact.observedAt
              : right.fact.observedAt,
          private: false,
          content: JSON.stringify({
            operation: 'absolute_price_difference',
            currency,
            operands: [
              { factId: first.fact.id, code: first.price.code, amountMinor: a },
              { factId: right.fact.id, code: right.price.code, amountMinor: b },
            ],
            amountMinor: serializedAmount,
            formattedAmount: formatExactMoney(amount, currency, 2),
            moreExpensiveCode:
              difference === 0n
                ? null
                : difference > 0n
                  ? first.price.code
                  : right.price.code,
          }),
        });
      }
    }
  }
  return comparisons;
}
